import { ReactNode } from 'react'
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary'
import { Card, Container, Typography, Alert, AlertTitle } from '@mui/material'
import { captureReactException } from '@sentry/react'

type ErrorScreenProps = {
  error: Error
}

function ErrorScreen({ error }: ErrorScreenProps) {
  return (
    <Container maxWidth="sm">
      <Card sx={{ my: 2 }}>
        <Alert severity="error" variant="outlined">
          <AlertTitle>Something went wrong.</AlertTitle>
          Please try again later.
          {import.meta.env.VITE_ENVIRONMENT === 'dev' && (
            <>
              <Typography
                component="div"
                variant="body2"
                color="error"
                sx={{ overflow: 'auto' }}
              >
                <code>
                  <pre>{error.stack}</pre>
                </code>
              </Typography>

              <Typography variant="caption" color="text.secondary">
                Note: Error information is only shown in dev environment.
              </Typography>
            </>
          )}
        </Alert>
      </Card>
    </Container>
  )
}

type ErrorBoundaryProps = {
  children: ReactNode
}

export default function ErrorBoundary({ children }: ErrorBoundaryProps) {
  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorScreen}
      onError={(error, info) => {
        captureReactException(error, info)
      }}
    >
      {children}
    </ReactErrorBoundary>
  )
}
